import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, UsersThree, CheckCircle, Warning } from "@phosphor-icons/react";
import { API } from "../../App";

const REASONS = [
  { key: "workload_balancing", label: "Workload balancing" },
  { key: "manager_reassignment", label: "Manager reassignment" },
  { key: "leave_absence", label: "Leave / Absence" },
  { key: "resignation_handover", label: "Resignation handover" },
  { key: "product_category_mismatch", label: "Product category mismatch" },
  { key: "location_mismatch", label: "Location mismatch" },
  { key: "duplicate_assignment", label: "Duplicate assignment" },
  { key: "other", label: "Other" },
];

const STATUS_OPTIONS = ["", "new", "contacted", "qualified", "proposal_sent", "negotiation", "won", "lost"];
const PRIORITY_OPTIONS = ["", "hot", "warm", "cold"];

export default function BulkTransferModal({ open, user, onClose, onCompleted, leadSources: leadSourcesProp, followupStatuses: followupStatusesProp }) {
  const [users, setUsers] = useState([]);
  const [leadSources, setLeadSources] = useState(leadSourcesProp || []);
  const [followupStatuses, setFollowupStatuses] = useState(followupStatusesProp || []);
  const [fromOwnerId, setFromOwnerId] = useState("");
  const [toOwnerId, setToOwnerId] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [source, setSource] = useState("");
  const [followupStatus, setFollowupStatus] = useState("");
  const [assigned, setAssigned] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reason, setReason] = useState("workload_balancing");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);     // {count, samples, from_owner_name}
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);

  const role = (user?.role || "").toLowerCase();

  useEffect(() => {
    if (!open) return;
    setFromOwnerId(""); setToOwnerId(""); setStatus(""); setPriority(""); setSource("");
    setFollowupStatus(""); setAssigned(""); setDateFrom(""); setDateTo("");
    setReason("workload_balancing"); setNote(""); setPreview(null);
    api.get(`/users`).then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    // Self-fetch source + follow-up status masters if not provided by parent
    if (!leadSourcesProp || leadSourcesProp.length === 0) {
      axios.get(`${API}/leads/sources`, { params: { requester_role: user?.role || "" } })
        .then((r) => setLeadSources(r.data || []))
        .catch(() => setLeadSources([]));
    }
    if (!followupStatusesProp || followupStatusesProp.length === 0) {
      axios.get(`${API}/leads/follow-up-statuses`, { params: { requester_role: user?.role || "" } })
        .then((r) => setFollowupStatuses(r.data || []))
        .catch(() => setFollowupStatuses([]));
    }
  }, [open]);

  const bdes = useMemo(() => (users || []).filter((u) => ["bde", "bdm"].includes((u.role || "").toLowerCase())), [users]);

  const filterPayload = useMemo(() => {
    const f = {};
    if (fromOwnerId) f.from_owner_id = fromOwnerId;
    if (status) f.status = status;
    if (priority) f.priority = priority;
    if (source) f.source = source;
    if (followupStatus) f.followup_status = followupStatus;
    if (assigned) f.assigned = assigned;
    if (dateFrom) f.date_from = dateFrom;
    if (dateTo) f.date_to = dateTo;
    return f;
  }, [fromOwnerId, status, priority, source, followupStatus, assigned, dateFrom, dateTo]);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`${API}/leads/transfer/bulk-preview`, {
        filters: filterPayload,
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const execute = async () => {
    if (!toOwnerId) { toast.error("Select destination owner"); return; }
    if (!preview || preview.count === 0) { toast.error("Run preview first"); return; }
    if (!window.confirm(`Confirm: ${preview.count} leads will be transferred${preview.from_owner_name ? ` from ${preview.from_owner_name}` : ""}.`)) return;
    setExecuting(true);
    try {
      const { data } = await api.post(`${API}/leads/transfer/bulk-execute`, {
        filters: filterPayload,
        to_owner_id: toOwnerId,
        reason, note,
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      if (data?.status === "pending") {
        toast.success(`Bulk transfer submitted for approval (${data.preview_count} leads)`);
      } else if (data?.status === "completed") {
        toast.success(`Bulk transfer completed (${data.executed_count} of ${data.preview_count} leads)`);
      } else if (data?.status === "noop") {
        toast.info("No matching leads to transfer");
      }
      onCompleted && onCompleted();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk transfer failed");
    } finally {
      setExecuting(false);
    }
  };

  if (!open) return null;

  const ownerName = (id) => (users.find((u) => u.user_id === id)?.name) || "";
  const previewLabel = preview?.from_owner_name
    ? `${preview.count} leads will be transferred from ${preview.from_owner_name}${toOwnerId ? ` to ${ownerName(toOwnerId)}` : ""}`
    : `${preview?.count ?? 0} matching leads`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="bulk-transfer-modal">
      <div className="bg-white border border-slate-200 max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UsersThree size={20} className="text-[#002FA7]" />
            <h3 className="text-lg font-bold">Bulk Transfer Leads</h3>
          </div>
          <button onClick={onClose} data-testid="bulk-transfer-close" className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">From BDE / BDM</label>
              <select data-testid="bulk-from-owner" value={fromOwnerId} onChange={(e) => { setFromOwnerId(e.target.value); setPreview(null); }}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                <option value="">— Any —</option>
                {bdes.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({(u.role || "").toUpperCase()})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">To BDE / BDM *</label>
              <select data-testid="bulk-to-owner" value={toOwnerId} onChange={(e) => setToOwnerId(e.target.value)}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                <option value="">— Select —</option>
                {bdes.filter((u) => u.user_id !== fromOwnerId).map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({(u.role || "").toUpperCase()})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Status</label>
              <select data-testid="bulk-status" value={status} onChange={(e) => { setStatus(e.target.value); setPreview(null); }}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || "— Any —"}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Priority</label>
              <select data-testid="bulk-priority" value={priority} onChange={(e) => { setPriority(e.target.value); setPreview(null); }}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                {PRIORITY_OPTIONS.map((s) => <option key={s} value={s}>{s || "— Any —"}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Source</label>
              <select data-testid="bulk-source" value={source} onChange={(e) => { setSource(e.target.value); setPreview(null); }}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                <option value="">— Any —</option>
                {leadSources.map((s) => <option key={s.slug || s.name} value={s.slug || s.name}>{s.name || s.slug}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Follow-up Status</label>
              <select data-testid="bulk-fu-status" value={followupStatus} onChange={(e) => { setFollowupStatus(e.target.value); setPreview(null); }}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                <option value="">— Any —</option>
                {(followupStatuses || []).map((s) => <option key={s.slug || s.id || s.name} value={s.slug || s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Assigned</label>
              <select data-testid="bulk-assigned" value={assigned} onChange={(e) => { setAssigned(e.target.value); setPreview(null); }}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                <option value="">— Any —</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">From Date</label>
                <input type="date" data-testid="bulk-date-from" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreview(null); }}
                  className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">To Date</label>
                <input type="date" data-testid="bulk-date-to" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreview(null); }}
                  className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm" />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Reason *</label>
              <select data-testid="bulk-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm">
                {REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Note</label>
              <input type="text" data-testid="bulk-note" value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm" />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button data-testid="bulk-run-preview" onClick={runPreview} disabled={previewing}
              className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:bg-slate-100">
              {previewing ? "Calculating…" : "Preview Count"}
            </button>
            {preview && (
              <div className={`flex-1 flex items-center gap-2 px-3 py-2 text-xs ${preview.count === 0 ? "bg-slate-100 text-slate-600" : "bg-blue-50 text-blue-800"}`} data-testid="bulk-preview-summary">
                {preview.count > 0 ? <CheckCircle size={14} /> : <Warning size={14} />}
                <span className="font-medium">{previewLabel}</span>
              </div>
            )}
          </div>

          {preview && preview.count > 0 && (
            <div className="border border-slate-200 px-3 py-2 text-[11px] text-slate-700 space-y-1">
              <div className="font-bold text-slate-600 uppercase tracking-wider text-[10px]">Sample (first 10)</div>
              {(preview.samples || []).map((s) => (
                <div key={s.lead_id} className="font-mono">· {s.name} <span className="text-slate-400">({s.assigned_to || "—"})</span></div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="bulk-transfer-submit" onClick={execute} disabled={executing || !toOwnerId || !preview || preview.count === 0}
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300">
            {executing ? "Transferring…" : role === "bdm" ? "Submit for Approval" : "Confirm Bulk Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}
